import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Eraser, LinkIcon, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError } from "@/lib/api";
import { PhotoBox } from "@/components/PhotoBox";

const T = {
  es: { idTitle: "Foto de tu identificación (pasaporte) *", cardTitle: "Foto de la tarjeta con la que pagaste (opcional)", cardHint: "Tapa los números del centro y el código de seguridad (CVV); solo deben verse los últimos 4 dígitos y tu nombre.", camera: "Tomar foto", gallery: "Elegir de la galería", remove: "Quitar foto", noId: "Agrega la foto de tu identificación (pasaporte)", badImg: "No se pudo leer la imagen, intenta con otra", title: "Firma tu documento", intro: "Revisa el documento y firma con tu dedo en el recuadro.", sign: "Tu firma", clear: "Borrar", accept: "He leído y acepto los términos del documento", send: "Firmar documento", sending: "Firmando…", done: "¡Documento firmado!", doneText: "Gracias. Tu firma quedó guardada en el documento.", invalid: "El enlace para firmar no es válido o ya venció.", page: "Página", hint: "Firma aquí", empty: "Dibuja tu firma antes de enviar" },
  en: { idTitle: "Photo of your ID (passport) *", cardTitle: "Photo of the card you paid with (optional)", cardHint: "Cover the middle numbers and the security code (CVV); only the last 4 digits and your name should be visible.", camera: "Take photo", gallery: "Choose from gallery", remove: "Remove photo", noId: "Please add a photo of your ID (passport)", badImg: "Could not read the image, please try another one", title: "Sign your document", intro: "Review the document and sign with your finger in the box.", sign: "Your signature", clear: "Clear", accept: "I have read and accept the terms of the document", send: "Sign document", sending: "Signing…", done: "Document signed!", doneText: "Thank you. Your signature has been saved on the document.", invalid: "This signing link is not valid or has expired.", page: "Page", hint: "Sign here", empty: "Please draw your signature first" },
};

const SignPad = ({ canvasRef, onDraw, onReset, hint }) => {
  const drawing = useRef(false);
  useEffect(() => {
    const c = canvasRef.current;
    let lastW = 0;
    const size = () => {
      const r = c.getBoundingClientRect(); const s = window.devicePixelRatio || 1;
      if (Math.round(r.width) === lastW) return;
      lastW = Math.round(r.width);
      c.width = r.width * s; c.height = r.height * s;
      const ctx = c.getContext("2d"); ctx.scale(s, s); ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0B1F4D";
      onReset();
    };
    size();
    window.addEventListener("resize", size);
    return () => window.removeEventListener("resize", size);
  }, [canvasRef]); // eslint-disable-line react-hooks/exhaustive-deps
  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); try { canvasRef.current.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ } drawing.current = true; const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); onDraw(); };
  const up = () => { drawing.current = false; };
  return (
    <div className="relative">
      <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onPointerCancel={up}
        className="h-44 w-full touch-none rounded-xl border-2 border-dashed border-slate-300 bg-white" data-testid="sign-pad" />
      <span className="pointer-events-none absolute bottom-3 left-4 right-4 border-t border-slate-300 pt-1 text-[11px] uppercase tracking-widest text-slate-400">{hint}</span>
    </div>
  );
};

export default function SignDocument() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState("");
  const [idPhoto, setIdPhoto] = useState("");
  const [cardPhoto, setCardPhoto] = useState("");
  const pad = useRef(null);
  useEffect(() => { api.get(`/public/sign/${token}`).then((r) => setDoc(r.data)).catch(() => setErr(true)); }, [token]);
  const t = T[doc?.lang === "en" ? "en" : "es"];
  const clear = () => { const c = pad.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setDrawn(false); };
  const submit = async () => {
    if (!drawn) { setMsg(t.empty); return; }
    if (!idPhoto) { setMsg(t.noId); return; }
    setBusy(true); setMsg("");
    try { await api.post(`/public/sign/${token}`, { signature: pad.current.toDataURL("image/png"), accept, id_photo: idPhoto, card_photo: cardPhoto || null }); setDone(true); window.scrollTo(0, 0); }
    catch (e) { setMsg(apiError(e).message); } finally { setBusy(false); }
  };
  if (err) return <Center icon={<LinkIcon className="text-slate-400" size={34} />} title={T.es.invalid} text={T.en.invalid} testid="sign-invalid" />;
  if (!doc) return <div className="min-h-screen animate-pulse bg-slate-100" />;
  if (done) return <Center icon={<CheckCircle2 className="text-emerald-600" size={40} />} title={t.done} text={t.doneText} testid="sign-done" />;
  return (
    <div className="min-h-screen bg-[#F3F4F6] pb-16">
      <header className="bg-[#0B1F4D] px-5 pb-8 pt-10 text-white">
        <div className="mx-auto max-w-2xl">
          <p className="text-xs uppercase tracking-[0.25em] text-white/60">{doc.template_name}</p>
          <h1 className="mt-2 font-heading text-3xl font-semibold sm:text-4xl" data-testid="sign-title">{t.title}</h1>
          <p className="mt-2 text-sm text-white/75">{doc.guest_name} · {t.intro}</p>
        </div>
      </header>
      <main className="mx-auto -mt-4 max-w-2xl space-y-4 px-4">
        {doc.pages.map((src, i) => (
          <img key={i} src={src} alt={`${t.page} ${i + 1}`} className="w-full rounded-lg bg-white shadow-sm ring-1 ring-slate-200" data-testid={`sign-page-${i + 1}`} />
        ))}
        <PhotoBox id="id" title={t.idTitle} value={idPhoto} onChange={(v) => { setIdPhoto(v); setMsg(""); }} labels={t} onError={() => setMsg(t.badImg)} />
        <PhotoBox id="card" title={t.cardTitle} hint={t.cardHint} value={cardPhoto} onChange={setCardPhoto} labels={t} onError={() => setMsg(t.badImg)} />
        <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="sign-section">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 font-semibold text-slate-900"><PenLine size={18} />{t.sign}</p>
            <button type="button" onClick={clear} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="sign-clear-btn"><Eraser size={15} />{t.clear}</button>
          </div>
          <SignPad canvasRef={pad} onDraw={() => { setDrawn(true); setMsg(""); }} onReset={() => setDrawn(false)} hint={t.hint} />
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} className="mt-0.5" data-testid="sign-accept-checkbox" />{t.accept}
          </label>
          {msg && <p className="text-sm text-rose-600" data-testid="sign-error">{msg}</p>}
          <Button onClick={submit} disabled={busy || !accept} className="h-12 w-full rounded-full bg-[#2F5BFF] text-base hover:bg-[#1F45E0]" data-testid="sign-submit-btn">{busy ? t.sending : t.send}</Button>
        </section>
      </main>
    </div>
  );
}

const Center = ({ icon, title, text, testid }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#F3F4F6] px-6" data-testid={testid}>
    <div className="max-w-sm text-center">{icon && <div className="mb-4 flex justify-center">{icon}</div>}
      <h1 className="font-heading text-2xl font-semibold text-slate-900">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">{text}</p></div>
  </div>
);
